import { useForm } from "react-hook-form";
import MultiStepForm from "@/components/MultiStepForm";
import { useState, useEffect, useRef } from "react";
import { DateTime } from "luxon";
import { JobPostCloseFormInput, JobPostCloseFormProps } from "./types";
import { CloseHireDetailsStep } from "./steps/CloseHireDetailsStep";
import { EmployerInformationStep } from "./steps/EmployerInformationStep";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { useNavigate } from "react-router-dom";
import { Path, Params } from "@/router";
import { LinkProps } from "@generouted/react-router/client";
import { useGetJobDetailsPath } from "../_utils";
import { useJobPostCloseForm } from "../hooks/useJobPostCloseForm";

/**
 * Default values for the form when no existing job data is available
 */
const createDefaultValues = (): JobPostCloseFormInput => ({
  reason: "Filled",
  notes: "",
  closeDate: DateTime.now().toFormat("yyyy-MM-dd"),
  hiringStatus: "Filled",
  careerConsultantVisited: "Not Set",
  isPgaFacilityConversion: "Not Set",
  isPgaJobPlacementConversion: "Not Set",
  hireIsPgaPro: "Not Sure",
  isNetNewPosition: "Not Set",
  hireCompensationAmount: "0",
  hireCompensationSchedule: "none",
  previousCompensation: "0",
  sendEmployerSurvey: true,
  hireeId: "",
  employeeData: null,
  employerData: null
});

/**
 * Form component for closing a job post
 */
export default function JobPostCloseForm({
  jobPostId,
  onSubmit,
  onSuccess,
  submitText = "Close Job",
  submitDialogContent = "Are you sure you want to close this job?",
  defaultValues,
  formRef: externalFormRef
}: JobPostCloseFormProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const internalFormRef = useRef<{ getValues: () => JobPostCloseFormInput } | null>(null);
  const formRef = externalFormRef || internalFormRef;
  const { handleSubmit: handleJobPostSubmit } = useJobPostCloseForm(jobPostId);

  const form = useForm<JobPostCloseFormInput>({
    mode: "onBlur",
    defaultValues: defaultValues || createDefaultValues()
  });

  const [isFormValid, setIsFormValid] = useState(false);

  // Update form validity when fields are blurred or changed
  useEffect(() => {
    setIsFormValid(form.formState.isValid);
  }, [form.formState.isValid]);

  // Expose form methods to parent component
  useEffect(() => {
    if (formRef) {
      formRef.current = {
        getValues: () => form.getValues()
      };
    }
  }, [formRef, form]);

  const jobDetailsPath = useGetJobDetailsPath(jobPostId);

  // Define the back link for navigation
  const backLink = {
    to: useGetJobDetailsPath(jobPostId),
    title: "Back"
  } as unknown as LinkProps<Path, Params>;

  const handleFormSubmit = async (data: JobPostCloseFormInput) => {
    setIsSubmitting(true);
    try {
      const result = await handleJobPostSubmit(data);

      if (result.data?.updateCloseJob?.success) {
        showSnackbar("Job closed");
        if (onSuccess) {
          onSuccess(jobPostId);
        } else {
          navigate(jobDetailsPath);
        }
      } else {
        showErrorSnackbar(result.data?.updateCloseJob?.message || "Failed to close job");
      }

      if (onSubmit) {
        await onSubmit(data);
      }
    } catch (error) {
      showErrorSnackbar("An error occurred while closing the job");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Define the form steps
  const steps = [
    {
      headline: "Hire Details",
      supportingText: "Step 1",
      requiredFields: ["hiringStatus"],
      content: <CloseHireDetailsStep form={form} />
    },
    {
      headline: "Employer Information",
      supportingText: "Step 2",
      requiredFields: ["careerConsultantVisited", "isPgaFacilityConversion", "isPgaJobPlacementConversion"],
      content: <EmployerInformationStep form={form} />
    }
  ];

  return (
    <MultiStepForm
      form={form}
      steps={steps}
      renderStepper={true}
      onSubmit={handleFormSubmit}
      backLink={backLink}
      modelName="Job"
      customHeadline="Close Job"
      enableSubmitOnLastStep={true}
      showActions={true}
      disabledSubmit={!isFormValid}
      submitText={submitText}
      submitDialogContent={submitDialogContent}
      isSubmitting={isSubmitting}
      moreActions={[]}
      onStepChange={() => {}}
    />
  );
}
